import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TemporalWorkflowService } from '../services/temporal-workflow-service';
import httpClient from '../services/http-client';
import { 
  ApprovalDecisionRequest,
  ApprovalResponse
} from '../types';

// Create a temporal workflow service instance using the default HTTP client
const temporalWorkflowService = new TemporalWorkflowService(httpClient);

/**
 * Hook for submitting approval decisions
 */
export function useSubmitApproval() {
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalResponse,
    Error,
    { divisionId: string; temporalWorkflowId: string; decision: ApprovalDecisionRequest }
  >({
    mutationFn: async ({ divisionId, temporalWorkflowId, decision }) =>
      temporalWorkflowService.submitApprovalDecision(divisionId, temporalWorkflowId, decision),
    onSuccess: (data, variables) => {
      // Invalidate and refetch orders requiring approval
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'requiring-approval', variables.divisionId] 
      });
      
      // Also invalidate pending approvals if they exist
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'pending-approvals', variables.divisionId] 
      });

      // Invalidate dashboard metrics
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'dashboard-summary', variables.divisionId] 
      });

      // Invalidate order detail queries to ensure quantity, line price, and order price update
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'detail', variables.divisionId] 
      });
    },
    onError: (error) => {
      console.error('Error submitting approval decision:', error);
    }
  });
}

/**
 * Hook for submitting batch approval decisions (processed in parallel)
 */
export function useSubmitBatchApproval() {
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalResponse,
    Error,
    { divisionId: string; temporalWorkflowId: string; decisions: ApprovalDecisionRequest[] }
  >({
    mutationFn: async ({ divisionId, temporalWorkflowId, decisions }) =>
      temporalWorkflowService.submitBatchApprovalDecisions(divisionId, temporalWorkflowId, decisions),
    onSuccess: (data, variables) => {
      // Invalidate and refetch orders requiring approval
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'requiring-approval', variables.divisionId] 
      });
      
      // Also invalidate pending approvals if they exist
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'pending-approvals', variables.divisionId] 
      });

      // Invalidate dashboard metrics
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'dashboard-summary', variables.divisionId] 
      });

      // Invalidate order detail queries to ensure quantity, line price, and order price update
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'detail', variables.divisionId] 
      });
    },
    onError: (error) => {
      console.error('Error submitting batch approval decisions:', error);
    }
  });
}

/**
 * Hook for cancelling workflows
 */
export function useCancelWorkflow() {
  const queryClient = useQueryClient();

  return useMutation<
    ApprovalResponse,
    Error,
    { divisionId: string; temporalWorkflowId: string; reason: string }
  >({
    mutationFn: async ({ divisionId, temporalWorkflowId, reason }) =>
      temporalWorkflowService.cancelWorkflow(divisionId, temporalWorkflowId, reason),
    onSuccess: (data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'requiring-approval', variables.divisionId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'pending-approvals', variables.divisionId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['orders', 'dashboard-summary', variables.divisionId] 
      });
    },
    onError: (error) => {
      console.error('Error cancelling workflow:', error);
    }
  });
}

/**
 * Hook for checking whether an order has missing required fields at its current
 * approval step, without submitting a decision.
 */
export function useCheckRequiredFields() {
  return useMutation<
    { missingFields: string[] },
    Error,
    { divisionId: string; temporalWorkflowId: string }
  >({
    mutationFn: async ({ divisionId, temporalWorkflowId }) =>
      temporalWorkflowService.checkRequiredFields(divisionId, temporalWorkflowId),
    onError: (error) => {
      console.error('Error checking required fields:', error);
    }
  });
}
